import { CaretRightIcon } from '@phosphor-icons/react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { cn } from 'app/lib/utils'
import type { RailGroup } from 'lib/railSections'

/**
 * A page's sections (Settings, Ops), grouped. `rail` is the column inside the well on desktop,
 * styled like the sidebar's own rows; `list` is the full-width page a phone opens on the page's
 * home path.
 */
export function SectionRail({
  title,
  groups,
  variant,
}: {
  title: string
  groups: RailGroup[]
  variant: `rail` | `list`
}): ReactElement {
  const list = variant === `list`
  return (
    <nav aria-label={title} className={cn(`flex flex-col`, list ? `gap-5` : `gap-3`)}>
      {/* On a phone the list is the whole page, so it carries the title; beside the main nav the
          sidebar row already names it. */}
      {list && <h2 className="gilroy text-heading-md text-strong">{title}</h2>}
      {groups.map((group) => (
        <section key={group.id} className="flex flex-col gap-0.5">
          <h3 className="px-2 pb-1 text-label-xs uppercase tracking-wide text-strong">{group.label}</h3>
          <div
            className={cn(
              list && `divide-y divide-default overflow-hidden rounded-xl border border-default bg-surface`,
            )}
          >
            {group.items.map(({ key, label, href, active, Icon }) => (
              <Link
                key={key}
                to={href}
                aria-current={active ? `page` : undefined}
                className={cn(
                  `flex items-center gap-x-2`,
                  list ? `h-12 px-3 text-heading-xs text-strong` : `h-9 rounded-md px-2 text-body-sm`,
                  // Quieter than the main nav beside it, so the two columns do not compete: muted
                  // at rest, and only the section you are on reads strong.
                  !list &&
                    (active
                      ? `bg-(--fg-strong)/6 font-medium text-strong`
                      : `text-subtle hover:bg-(--fg-strong)/4 hover:text-default`),
                )}
              >
                <Icon size={18} weight={list ? `bold` : `regular`} className={cn(`shrink-0`, list && `text-default`)} />
                <span className="min-w-0 grow truncate">{label}</span>
                {list && <CaretRightIcon size={14} weight="bold" className="shrink-0 text-minimal" />}
              </Link>
            ))}
          </div>
        </section>
      ))}
    </nav>
  )
}
